import { Suspense } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { Loader } from '@givebutter/primitives/loader';
import * as Tabs from '@givebutter/primitives/tabs';
import { useToast } from '@givebutter/primitives/toast';
import { faDownload, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FetchCampaignInvitesParams, fetchCampaignInvites } from 'api/campaigns';
import { generateExports } from 'api/contacts/exports';
import * as ExportDataDialog from 'assets/js/components/ExportDataDialog';
import { CampaignMembersInviteTable } from 'assets/js/pages/campaign-settings/campaign-members/CampaignMembersInviteTable';
import {
    CampaignMembersProvider,
    CampaignMembersTab,
    useCampaignMembers,
} from 'assets/js/pages/campaign-settings/campaign-members/CampaignMembersProvider';
import { CampaignMembersSplash } from 'assets/js/pages/campaign-settings/campaign-members/CampaignMembersSplash';
import { CampaignMembersTable } from 'assets/js/pages/campaign-settings/campaign-members/CampaignMembersTable';
import * as InviteReminderDialog from 'assets/js/pages/campaign-settings/campaign-members/InviteReminderDialog';
import * as Input from 'components/input';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';

const CampaignMembersContent = () => {
    const toast = useToast();
    const history = useHistory();
    const match = useRouteMatch();
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const { search, setSearch, currentTab, setCurrentTab } = useCampaignMembers();
    const campaignId = campaign.id;

    const invitesParams: FetchCampaignInvitesParams = { filter: 'pending' };

    const { mutateAsync: exportMembers } = useMutation({
        mutationFn: () =>
            generateExports(accountId, {
                scope: 'all',
                object: 'campaign',
                report_type: 'csv',
                resource: 'members',
                object_id: campaignId,
            }),
        onError: () => {
            toast.error('Unable to generate report');
        },
    });

    const { data: initialPendingInvites } = useSuspenseQuery({
        queryKey: campaignQueryKeys.invitesWithFilters(campaignId, invitesParams),
        queryFn: () => fetchCampaignInvites(campaignId, invitesParams),
    });

    if (campaign.disableTeamFundraising?.active) {
        return <CampaignMembersSplash />;
    }

    return (
        <div className={'flex flex-col gap-4'}>
            <Tabs.Root
                value={currentTab}
                onValueChange={(value: string) => {
                    setSearch('');
                    setCurrentTab(value as CampaignMembersTab);
                }}
            >
                <div
                    className={'flex flex-wrap items-stretch justify-between gap-4 sm:flex-nowrap'}
                >
                    <div className="flex flex-col gap-4">
                        <p className={'text-2xl font-medium'}>Individuals</p>
                        <div className="flex flex-col gap-4">
                            <Tabs.List>
                                <Tabs.Trigger value={'individuals'}>
                                    <div className={'flex items-center gap-1.5'}>
                                        <p>Individuals</p>
                                    </div>
                                </Tabs.Trigger>
                                <Tabs.Trigger value={'invites'}>
                                    <div className={'flex items-center gap-1.5'}>Invites</div>
                                </Tabs.Trigger>
                            </Tabs.List>
                        </div>
                    </div>
                    {currentTab === 'invites' &&
                        initialPendingInvites?.data &&
                        initialPendingInvites.data.length > 0 && (
                            <div className="flex flex-1 flex-col gap-4">
                                <InviteReminderDialog.Root />
                            </div>
                        )}
                    <div className="flex flex-col items-end gap-4">
                        <div className={'flex flex-wrap gap-2 sm:flex-nowrap'}>
                            <ExportDataDialog.Root label={'Individuals'} onSubmit={exportMembers}>
                                <ExportDataDialog.Trigger asChild={true}>
                                    <Button variant={'outline'} color={'neutral'}>
                                        <FontAwesomeIcon icon={faDownload} />
                                        <p>Export</p>
                                    </Button>
                                </ExportDataDialog.Trigger>
                            </ExportDataDialog.Root>

                            <Button
                                variant="solid"
                                onClick={() => history.push(`${match.url}/new`)}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                Add individual
                            </Button>
                        </div>
                        {(currentTab === 'individuals' || currentTab === 'invites') && (
                            <div className="flex w-full flex-col gap-2">
                                <Label className="sr-only" htmlFor="campaign_members_search">
                                    Search
                                </Label>
                                <Input.Root>
                                    <Input.SearchIcon />
                                    <Input.Form
                                        id="campaign_members_search"
                                        value={search}
                                        placeholder={'Search'}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </Input.Root>
                            </div>
                        )}
                    </div>
                </div>
                <Suspense fallback={<Loader />}>
                    <Tabs.Content value="individuals">
                        <CampaignMembersTable />
                    </Tabs.Content>
                    <Tabs.Content value="invites">
                        <CampaignMembersInviteTable />
                    </Tabs.Content>
                </Suspense>
            </Tabs.Root>
        </div>
    );
};

const CampaignMembers = () => {
    return (
        <CampaignMembersProvider>
            <CampaignMembersContent />
        </CampaignMembersProvider>
    );
};

export { CampaignMembers };
